import LocationDetails from "@/components/steps/LocationDetails";
import SpecialItems from "@/components/steps/SpecialItems";
import {useAppState} from "@/store";
import DatePicker from "@/components/steps/DatePicker";
import ContactDetails from "@/components/steps/ContactDetails";
import Estimate from "@/components/steps/Estimate";
import {useCallback, useEffect, useRef, useState} from "react";
import gsap from 'gsap';
import {useGSAP} from '@gsap/react';
import BookingBar from "@/components/BookingBar";
import ProgressBar from "@/components/ProgressBar";
import {debounce, loadState, saveState, scrollToTop} from "@/helpers";
import Book from "@/components/steps/Book";
import Confirmation from "@/components/steps/Confirmation";
import {useValidation} from "@/validation";
import {toast} from "react-toastify";

export default function Steps() {

    const {state, setState} = useAppState()
    const {validate} = useValidation()

    const [loaded, setLoaded] = useState(false);

    const container = useRef(null)
    const stepWrapper = useRef(null)

    const { contextSafe } = useGSAP({ scope: container });

    const goToStep = async (step, doPushState = true) => {

        const transitionStep = contextSafe(async () => {
            await gsap.fromTo(stepWrapper.current, {
                opacity: 1, ease: "power3.in", scale: 1, duration: 0.125
            }, {
                opacity: 0, scale: 0.95
            })

            setState(s => {
                s.currentStep = step
            })

            await gsap.fromTo(stepWrapper.current, {
                opacity: 0, scale: 0.95, ease: "power3.out", duration: 0.125
            }, {
                opacity: 1, scale: 1
            })
        })

        toast.dismiss()

        console.log ('going to step', step)

        scrollToTop()

        if (doPushState) {
            history.pushState({step: step}, '', `#step-${step}`)
        }

        await transitionStep()
    }

    const onPop = async (e) => {
        const step = e.state?.step || 1
        await goToStep(step, false)
    }

    const debouncedSave = useCallback(debounce(saveState, 250), [])

    useEffect(() => {
        window.addEventListener('popstate', onPop)
        return () => window.removeEventListener('popstate', onPop)
    }, [onPop]);

    useEffect(() => {
        debouncedSave(state);
    }, [state]);

    useEffect(() => {

        // load saved form data from session if available
        let data = loadState()

        // remove any hash values from the URL
        history.pushState("", "", window.location.pathname + window.location.search);

        // apply saved state values
        if (data && !data.complete) {
            setState(s => {
                for (const property in data) {
                    s[property] = data[property]
                }
            })
        }

        setState(s => {
            s.initialized = true
        })

        setLoaded(true)
    }, []);

    if (!loaded) {
        return
    }

    let steps = {
        1: <LocationDetails goToStep={goToStep}/>,
        2: <SpecialItems goToStep={goToStep}/>,
        3: <DatePicker goToStep={goToStep}/>,
        4: <ContactDetails goToStep={goToStep}/>,
        5: <Estimate/>,
        6: <Book goToStep={goToStep}/>,
        7: <Confirmation/>
    }

    let labels = [
        'Location Details',
        'Special Items',
        'Date & Time',
        'Contact Info',
        'Estimate',
        'Book'
    ]

    // display alternate flow of steps if alternate mode is set

    // if (false && window.ein?.content?.alt) {
    //     steps = {
    //         1: <LocationDetails goToStep={goToStep}/>,
    //         2: <SpecialItems goToStep={goToStep}/>,
    //         3: <DatePicker goToStep={goToStep}/>,
    //         4: <Estimate/>,
    //         5: <ContactDetails goToStep={goToStep}/>,
    //         6: <Book goToStep={goToStep}/>,
    //         7: <Confirmation/>
    //     }
    //
    //     labels = [
    //         'Location Details',
    //         'Special Items',
    //         'Date & Time',
    //         'Estimate',
    //         'Contact Info',
    //         'Book'
    //     ]
    // }

    return (
        <div ref={container}>
            {state.currentStep < 7 && <ProgressBar labels={labels} goToStep={goToStep}/>}
            <div ref={stepWrapper} className={"py-[40px] lg:py-[56px]"}>
                {steps[state.currentStep]}
            </div>
            {state.currentStep === 5 && <BookingBar goToStep={goToStep}/>}
        </div>
    )
}
